import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import deliveryDroneImg from "../assests/delivery_drone.png";
import roverImg from "../assests/rover_custom.png";
import jetsonImg from "../assests/jetson_custom.png";
import piImg from "../assests/pi_custom.png";
import droneVideo from "../assests/drone_video.mp4";
import roverVideo from "../assests/rover_video.mp4";
import { assets } from "../assests/assests";

function ProductShowcase() {
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Prevent body scroll when full page modal is open
  useEffect(() => {
    if (selectedProduct) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedProduct]);

  // The 5 Proprietary Engineered Products
  const products = [
    {
      id: "uav-delivery",
      title: "Autonomous Delivery Drone",
      subtitle: "Medical & Goods Delivery Drone",
      image: "https://images.unsplash.com/photo-1579820010410-c10411aaaa88?q=80&w=800&auto=format&fit=crop", // Real heavy lift hexacopter drone
      video: droneVideo, // Real video of drone carrying parcel over Bikaner
      color: "#10b981", // Emerald
      shortDesc: "Engineered for high-speed, autonomous aerial delivery of critical medical supplies and emergency goods across difficult terrains.",
      specs: [
        { label: "Max Payload Capacity", value: "5.5 kg - 10 kg" },
        { label: "Operational Range", value: "25 km (Round Trip)" },
        { label: "Navigation System", value: "RTK GPS + LiDAR Obstacle Avoidance" },
        { label: "Cruising Speed", value: "65 km/h (Wind Resistant)" },
      ],
      detailedDesc: "Our Autonomous Delivery Drone is a heavy-duty, weather-resistant aerial vehicle designed specifically for rapid logistics. It integrates dual-redundant GPS, automated winch-drop mechanisms, and real-time cloud tracking to ensure safe, zero-human-intervention delivery to remote hospitals, farms, and industrial sites."
    },
    {
      id: "ag-rover",
      title: "Agri-Smart Farming Rover",
      subtitle: "Autonomous Farming & Soil Testing Rover",
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=800&auto=format&fit=crop", // Real robotics/rover image
      video: roverVideo, // Real video of rover spraying crops in field
      color: "#ef4444", // Red
      shortDesc: "All-terrain unmanned ground rover equipped with automated soil sampling probes, precision weed eradication, and LiDAR mapping.",
      specs: [
        { label: "Soil Sensors", value: "NPK, Moisture, pH & Temp Probes" },
        { label: "Terrain Mobility", value: "4x4 / 6x6 Rock-Crawler Suspension" },
        { label: "AI Vision Core", value: "Onboard Deep Learning Weed Detection" },
        { label: "Battery Endurance", value: "8 Hours Continuous Field Run" },
      ],
      detailedDesc: "Designed for modern precision farming, the Agri-Smart Rover autonomously navigates crop rows using LiDAR and RTK guidance. It performs non-destructive soil testing, maps nutrient deficiencies in real-time, and applies micro-doses of organic treatments directly to target roots, reducing fertilizer waste by up to 60%."
    },
    {
      id: "snt-printer",
      title: "Smart NXV Printer",
      subtitle: "Scan, Cloud Upload & Instant Document Print",
      image: "https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?q=80&w=800&auto=format&fit=crop", // Real paper/office printer image
      color: "#3b82f6", // Blue
      shortDesc: "Automated document printing kiosk that features built-in optical scanning, direct cloud syncing, and high-speed precision printing.",
      specs: [
        { label: "Optical Scanner", value: "High-Res Document & ID Scanner" },
        { label: "Cloud Engine", value: "Direct Cloud Sync & Print Queue" },
        { label: "Print Speed", value: "Ultra-Fast Laser Printing" },
        { label: "Connectivity", value: "Wi-Fi, Ethernet & App Sync" },
      ],
      detailedDesc: "The SNT Smart Kiosk Printer revolutionizes document workflows. Simply upload your documents to the cloud or place them on the scanner bed, and our proprietary cloud engine automatically formats, queues, and prints them with high precision. Ideal for remote campuses and automated office environments."
    },
    {
      id: "motor-driver",
      title: "Custom Motor Driver PCB",
      subtitle: "In-House Designed Dual-H-Bridge Motor Driver",
      image: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=800&auto=format&fit=crop",
      color: "#f59e0b", // Amber
      shortDesc: "Custom-engineered high-efficiency motor control architecture designed for heavy rovers, industrial actuators, and brushless motors.",
      specs: [
        { label: "Current Rating", value: "40A Continuous / 80A Peak" },
        { label: "Voltage Range", value: "12V - 48V DC Input" },
        { label: "Thermal Control", value: "Active MOSFET Cooling & Temp Cutoff" },
        { label: "Control Interface", value: "CAN Bus, PWM, UART & RS485" },
      ],
      detailedDesc: "Built from scratch by NXV DYNAMICS engineers, this proprietary motor driver PCB delivers industrial-grade power management. Featuring ultra-low RDS(on) MOSFETs, hardware over-current protection, and seamless ROS/ROS2 telemetry integration, it serves as the muscular backbone for our robotics lineup."
    },
    {
      id: "ai-attendance",
      title: "Smart AI Attendance System",
      subtitle: "Biometric Vision & Contactless Attendance Portal",
      image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=800&auto=format&fit=crop",
      color: "#a855f7", // Purple
      shortDesc: "High-speed facial recognition and dual-frequency RFID attendance terminal engineered for schools, industrial factories, and corporate campuses.",
      specs: [
        { label: "Recognition Speed", value: "< 0.2 Seconds per Person" },
        { label: "Anti-Spoofing", value: "3D Infrared Liveness Detection" },
        { label: "Cloud Sync", value: "Real-time HRMS & Payroll Push" },
        { label: "Capacity", value: "50,000 Offline Face Templates" },
      ],
      detailedDesc: "Our standalone Smart Attendance Terminal combines edge AI computer vision with industrial robustness. It operates flawlessly in low light, detects masked individuals with 99.7% accuracy, and synchronizes attendance records directly to decentralized cloud servers without requiring continuous internet connectivity."
    }
  ];

  return (
    <div className="py-24 bg-[#f8fafc] text-[#0f172a] relative overflow-hidden font-sans border-b border-gray-200">
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Simple, Direct Section Header - Modern Left-Aligned Corporate Layout */}
        <div className="text-left max-w-3xl mb-14">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-tight mb-4 text-gray-950 font-sans">
            Our Products
          </h2>
          <p className="text-lg text-gray-600 font-normal leading-relaxed">
            Explore our proprietary hardware solutions. Click on any product card to open the full-screen interactive simulation & technical specifications.
          </p>
        </div>

        {/* 5 Products Grid in Apple Luxury Off-White Theme (NO FAKE TAGS/BADGES!) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.01 }}
              onClick={() => setSelectedProduct(item)}
              className="bg-white rounded-3xl p-5 sm:p-6 border border-gray-200/80 hover:border-gray-300 shadow-[0_15px_35px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_45px_rgba(0,0,0,0.1)] transition-all duration-300 cursor-pointer relative overflow-hidden flex flex-col justify-between group"
            >
              <div>
                {/* Product Preview Box - Reduced Height */}
                <div className="h-36 sm:h-44 w-full bg-gray-950 rounded-2xl border border-gray-200/60 mb-4 relative overflow-hidden flex items-center justify-center group-hover:border-gray-400 transition-all shadow-sm">
                  <img 
                    src={item.image} 
                    alt={item.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                  
                  {/* Subtle Gradient Overlay for luxury contrast */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60"></div>
                  
                  {/* Subtle Glow Behind Image */}
                  <div 
                    className="absolute w-28 h-28 rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-opacity pointer-events-none"
                    style={{ backgroundColor: item.color }}
                  ></div>
                </div>

                <h3 className="text-xl font-extrabold text-gray-900 mb-1.5 tracking-wide group-hover:text-black transition-colors">
                  {item.title}
                </h3>
                <p className="text-xs font-bold font-mono mb-3" style={{ color: item.color }}>
                  {item.subtitle}
                </p>
                <p className="text-gray-600 text-sm leading-relaxed font-normal line-clamp-3 mb-4">
                  {item.shortDesc}
                </p>
              </div>

              {/* Action Button at bottom of card */}
              <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-[10px] sm:text-xs font-mono font-bold uppercase tracking-wider text-gray-500 group-hover:text-gray-900">
                <span>View Full Page & Video</span>
                <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center group-hover:bg-gray-900 group-hover:text-white transition-all">
                  &rarr;
                </span>
              </div>
            </motion.div>
          ))}
        </div>

      </div>

      {/* FULL-PAGE MODAL IN APPLE LUXURY OFF-WHITE THEME (`bg-[#f8fafc] text-[#0f172a]`) */}
      <AnimatePresence>
        {selectedProduct && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[99999] bg-[#f8fafc] text-[#0f172a] overflow-y-auto w-screen h-screen flex flex-col font-sans"
          >
            {/* Top Sticky Navigation Bar - Clean, Minimalist Edge-Style Arrow & No Extra Title Text */}
            <div className="sticky top-0 z-[100000] bg-white/90 backdrop-blur-md border-b border-gray-200/80 px-6 py-4 flex items-center justify-between shadow-sm">
              <button 
                onClick={() => setSelectedProduct(null)}
                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300/80 flex items-center justify-center text-xl text-gray-800 transition-all shadow-sm"
                title="Back to Products"
              >
                &#8592;
              </button>

              {/* Clean Empty Center so there is NO extra green bullet or // OFFICIAL PREVIEW text */}
              <div className="text-sm font-extrabold text-gray-900 tracking-tight hidden sm:block">
                {selectedProduct.title}
              </div>
              
              <button 
                onClick={() => setSelectedProduct(null)}
                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300/80 flex items-center justify-center text-lg text-gray-800 transition-all shadow-sm"
                title="Close"
              >
                &#10005;
              </button>
            </div>

            {/* TOP WIDESCREEN CINEMATIC VIDEO / IMAGE BANNER (16:9 Aspect Video Fit!) */}
            <div className="w-full min-h-[380px] sm:min-h-[480px] relative bg-gray-100/60 border-b border-gray-200 flex flex-col items-center justify-center p-4 sm:p-8 overflow-hidden">
              
              {/* Widescreen 16:9 Video or Image Container (Fits 100% perfectly!) */}
              <div className="relative z-10 max-w-5xl w-full aspect-video rounded-3xl overflow-hidden border border-gray-300/80 shadow-[0_20px_50px_rgba(0,0,0,0.12)] my-2 bg-black flex items-center justify-center">
                {selectedProduct.video ? (
                  <video 
                    src={selectedProduct.video} 
                    autoPlay 
                    loop 
                    muted 
                    playsInline 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img 
                    src={selectedProduct.image} 
                    alt={selectedProduct.title} 
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

            </div>

            {/* BOTTOM FULL PAGE DETAILS & TECHNICAL SPECS (Clean Off-White Luxury Styling!) */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full flex-1">
              
              <div className="max-w-3xl mb-12">
                <h1 className="text-4xl sm:text-5xl font-black mb-3 text-gray-950 tracking-tight font-sans">
                  {selectedProduct.title}
                </h1>
                <p className="text-xl font-bold font-mono mb-6" style={{ color: selectedProduct.color }}>
                  {selectedProduct.subtitle}
                </p>
                <p className="text-gray-600 text-base sm:text-lg leading-relaxed font-normal">
                  {selectedProduct.detailedDesc}
                </p>
              </div>

              {/* Clean Specs Section */}
              <div className="mb-14">
                <h3 className="text-2xl font-extrabold text-gray-950 mb-6 tracking-tight flex items-center gap-3">
                  <span className="w-2 h-6 rounded-full inline-block" style={{ backgroundColor: selectedProduct.color }}></span>
                  <span>Technical Specifications</span>
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  {selectedProduct.specs.map((spec, i) => (
                    <div key={i} className="bg-white p-6 rounded-2xl border border-gray-200/80 hover:border-gray-300 transition-all shadow-sm">
                      <div className="text-xs font-mono text-gray-500 font-bold mb-1">{spec.label}</div>
                      <div className="text-lg font-black text-gray-900">{spec.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom Action Buttons */}
              <div className="flex flex-wrap items-center gap-5 pt-8 border-t border-gray-200">
                <a 
                  href="#contact" 
                  onClick={() => setSelectedProduct(null)}
                  className="px-10 py-4 rounded-full font-extrabold text-sm text-white transition-all shadow-md hover:scale-105"
                  style={{ backgroundColor: selectedProduct.color }}
                >
                  Request Technical Quotation &rarr;
                </a>
                <button 
                  onClick={() => setSelectedProduct(null)}
                  className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 border border-gray-300/80 text-lg font-bold transition-all text-gray-800 flex items-center justify-center shadow-sm"
                  title="Back"
                >
                  &#8592;
                </button>
              </div>

            </div>

          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}

export default ProductShowcase;
